import { HostsModel, VisitsModel, ZonesModel } from '@w3lcome/types';
import { getDb } from './SqliteDatabase';

interface UsersZonesTable {
  zoneId: string;
  cardNumber: string;
  mifareCardNumber: string;
  userZone: UserZone;
  userId: string;
}

interface UserZone {
  user: HostsModel | VisitsModel;
  zone: ZonesModel | null | undefined;
}

// Inicializa a tabela no banco de dados, caso ainda não exista.
const init = async () => {
  const db = getDb();
  await db.execAsync(`
    CREATE TABLE IF NOT EXISTS users_zones_table (
      zoneId TEXT NOT NULL,
      userId TEXT NOT NULL,
      cardNumber TEXT,
      mifareCardNumber TEXT,
      userZone TEXT
    );
  `);
};

// Cria um novo registro na tabela de usuários e zonas
const create = async (user: UsersZonesTable) => {
  const db = getDb();
  await db.withTransactionAsync(async (txn) => {
    await txn.executeSqlAsync(
      'INSERT INTO users_zones_table (zoneId, userId, cardNumber, mifareCardNumber, userZone) VALUES (?, ?, ?, ?, ?);',
      [
        user.zoneId,
        user.userId,
        user.cardNumber,
        user.mifareCardNumber,
        JSON.stringify(user.userZone),
      ]
    );
  });
};

// Deleta um registro específico de usuário e zona
const deleteOne = async (userId: string, zoneId: string) => {
  const db = getDb();
  await db.withTransactionAsync(async (txn) => {
    await txn.executeSqlAsync('DELETE FROM users_zones_table WHERE userId = ? AND zoneId = ?;', [
      userId,
      zoneId,
    ]);
  });
};

// Recupera um usuário com base no número do cartão e zona
const getOne = async (cardNumber: string, zoneId: string): Promise<UsersZonesTable | null> => {
  const db = getDb();
  let result: UsersZonesTable | null = null;

  await db.withTransactionAsync(async (txn) => {
    const { rows } = await txn.executeSqlAsync(
      'SELECT * FROM users_zones_table WHERE cardNumber = ? AND zoneId = ?;',
      [cardNumber, zoneId]
    );
    if (rows.length > 0) {
      const item = rows.item(0);
      const userZone = JSON.parse(item.userZone);
      result = { ...item, userZone };
    }
  });

  return result;
};

// Deleta todos os registros da tabela
const deleteAll = async () => {
  const db = getDb();
  await db.withTransactionAsync(async (txn) => {
    await txn.executeSqlAsync('DELETE FROM users_zones_table;', []);
  });
};

// Recupera um usuário com base no número do cartão mifare e zona
const getOneMifareCardNumber = async (
  mifareCardNumber: string,
  zoneId: string
): Promise<UsersZonesTable | null> => {
  const db = getDb();
  let result: UsersZonesTable | null = null;

  await db.withTransactionAsync(async (txn) => {
    const { rows } = await txn.executeSqlAsync(
      'SELECT * FROM users_zones_table WHERE mifareCardNumber LIKE ? AND zoneId = ?;',
      [`%|${mifareCardNumber}|%`, zoneId]
    );
    if (rows.length > 0) {
      const item = rows.item(0);
      const userZone = JSON.parse(item.userZone);
      result = { ...item, userZone };
    }
  });

  return result;
};

// Recupera todos os usuários na tabela
const all = async (): Promise<UsersZonesTable[]> => {
  const db = getDb();
  const users: UsersZonesTable[] = [];

  await db.withTransactionAsync(async (txn) => {
    const { rows } = await txn.executeSqlAsync('SELECT * FROM users_zones_table;', []);
    for (let i = 0; i < rows.length; ++i) {
      const item = rows.item(i);
      const userZone = JSON.parse(item.userZone);
      users.push({ ...item, userZone });
    }
  });

  return users;
};

// Deleta a tabela inteira
const deleteTable = async () => {
  const db = getDb();
  await db.execAsync(`
    DROP TABLE IF EXISTS users_zones_table;
  `);
};

export default {
  create,
  all,
  deleteTable,
  deleteOne,
  init,
  getOne,
  getOneMifareCardNumber,
  deleteAll,
};
